/**
 * Shared contact prefill — promotes the `<LeadForm>` prefill pattern to
 * a generic hook so EVERY contact-collecting surface (listing modals,
 * landing-page hero forms, OnDemand wizard, valuation calculator,
 * invite flow, B2B/B2C hero forms, etc.) can hydrate name/phone/email
 * from the same source of truth.
 *
 * Two prefill sources, merged with authed beating anon:
 *
 *   1. Authed user profile (`useAuthProvider().currentUser`). Always
 *      preferred when signed in — `firstname` + `lastname` compose
 *      `name`, `phoneNumber` and `email` map 1:1. Server-side state
 *      wins over a stale localStorage entry.
 *   2. Anonymous localStorage (`spotme_lead_prefill_v1`, 30-day TTL).
 *      Captured on any successful contact-form submit and replayed on
 *      every subsequent contact form. Written via `useContactPersist`.
 *
 * SSR safety (the whole reason this is a hook):
 *
 *   localStorage is only readable on the client, but a hydration
 *   render runs on both server and client and they MUST emit the same
 *   markup. We guard the localStorage read behind a `hasHydrated`
 *   useEffect so the first render is identical on both sides and the
 *   prefill arrives quietly on the post-hydration commit. Hand-rolled
 *   forms that adopt this hook get the SSR safety for free.
 *
 * Companion: `useContactPersist` — call this on form submit to mirror
 * the contact fields into localStorage so the NEXT form anywhere on
 * the site prefills. Forms with split firstName/lastName can pass
 * either `name` (composed) or `firstName + lastName` — the persist
 * helper composes when needed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuthProvider } from 'src/context/AuthProvider'
import {
  clearLeadContactPrefill,
  readLeadContactPrefill,
  writeLeadContactPrefill
} from 'src/lib/leadFormStorage'

export type ContactPrefill = {
  name?: string
  phone?: string
  email?: string
}

export type ContactPrefillSource = 'authed' | 'localStorage' | 'none'

export type UseContactPrefillReturn = {
  /**
   * Merged contact prefill. Empty object during SSR + the hydration
   * render. Authed user fields beat localStorage fields when both are
   * present.
   */
  prefill: ContactPrefill
  /**
   * True after the first client effect commits. Forms should gate any
   * RHF `setValue` / state-machine `setField` injection on this flag
   * — injecting earlier would race with the locked initial-values
   * snapshot and clobber user-typed input.
   */
  hasHydrated: boolean
  /**
   * Which source supplied the prefill. Drives the "¿No eres tú?" link
   * (only shown when source is `'localStorage'` — authed users can't
   * "not be themselves" without signing out, and `'none'` has nothing
   * to clear).
   */
  source: ContactPrefillSource
  /**
   * Whether a `currentUser` is present in the auth provider. Use this
   * to gate the write-back to localStorage: authed users' canonical
   * data lives server-side, so we don't want to mirror their info
   * into the shared anon prefill store (would leak the previous
   * authed user's data to the next anon visitor on a shared device).
   * Note: this is independent of `source` — a currentUser may exist
   * but with empty fields, in which case `source` is `'none'` or
   * `'localStorage'` but `isAuthed` is still true.
   */
  isAuthed: boolean
  /**
   * Clear the localStorage entry. Does NOT touch authed prefill (that
   * derives from `currentUser` and is always re-applied on next
   * render). Use this from the "¿No eres tú?" affordance.
   */
  clearAnonPrefill: () => void
}

export function useContactPrefill(): UseContactPrefillReturn {
  const { currentUser } = useAuthProvider()

  const [hasHydrated, setHasHydrated] = useState(false)
  useEffect(() => {
    setHasHydrated(true)
  }, [])

  const authedPrefill = useMemo<ContactPrefill | null>(() => {
    if (!currentUser) return null
    const firstname = (currentUser.firstname ?? '').trim()
    const lastname = (currentUser.lastname ?? '').trim()
    const name = [firstname, lastname].filter(Boolean).join(' ').trim()
    const phone = (currentUser.phoneNumber ?? '').trim()
    const email = (currentUser.email ?? '').trim()
    if (!name && !phone && !email) return null
    const out: ContactPrefill = {}
    if (name) out.name = name
    if (phone) out.phone = phone
    if (email) out.email = email
    return out
  }, [currentUser])

  const anonPrefill = useMemo<ContactPrefill | null>(() => {
    if (!hasHydrated) return null
    if (currentUser != null) return null
    const stored = readLeadContactPrefill()
    if (!stored) return null
    const out: ContactPrefill = {}
    if (stored.name) out.name = stored.name
    if (stored.phone) out.phone = stored.phone
    if (stored.email) out.email = stored.email
    return Object.keys(out).length === 0 ? null : out
  }, [hasHydrated, currentUser])

  const prefill = useMemo<ContactPrefill>(
    () => ({
      ...(anonPrefill ?? {}),
      ...(authedPrefill ?? {})
    }),
    [anonPrefill, authedPrefill]
  )

  const source: ContactPrefillSource =
    authedPrefill != null
      ? 'authed'
      : anonPrefill != null
        ? 'localStorage'
        : 'none'

  const clearAnonPrefill = useCallback(() => {
    clearLeadContactPrefill()
  }, [])

  return {
    prefill,
    hasHydrated,
    source,
    isAuthed: currentUser != null,
    clearAnonPrefill
  }
}

/**
 * Input shape accepted by `useContactPersist`. Mirrors `ContactPrefill`
 * but also accepts a split first/last-name pair — many supply-side
 * forms (TraditionalHeroForm, B2BHeroForm, Apply) collect first +
 * last separately. The persist helper composes them into a single
 * `name` before writing so the read side stays uniform.
 */
export type ContactPersistInput = ContactPrefill & {
  firstName?: string
  lastName?: string
}

/**
 * Returns a stable callback that writes contact info to the shared
 * prefill store. Call it from your form's submit handler AFTER the
 * mutation resolves so a network failure doesn't leave junk in
 * localStorage. The helper:
 *
 *   - composes `name` from `firstName + lastName` if the caller didn't
 *     provide `name` directly
 *   - trims whitespace
 *   - skips the write when ALL fields are empty (no-op so we don't
 *     replace good data with nothing)
 */
export function useContactPersist(): (values: ContactPersistInput) => void {
  return useCallback((values: ContactPersistInput) => {
    const composedName =
      (values.name ?? '').trim() ||
      [values.firstName ?? '', values.lastName ?? '']
        .map((p) => p.trim())
        .filter(Boolean)
        .join(' ')
    const name = composedName.trim()
    const phone = (values.phone ?? '').trim()
    const email = (values.email ?? '').trim()
    if (!name && !phone && !email) return
    writeLeadContactPrefill({ name, phone, email })
  }, [])
}
